import React from 'react';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useController } from 'react-hook-form';

interface HookTextAreaProps {
  name: string;
  control: any;
  label: string;
  rows: number;
  [key: string]: any;
  defaultValue?: string;
  isRequired?: boolean;
  customError?: boolean;
}

const HookTextArea: React.FC<HookTextAreaProps> = ({
  name,
  control,
  label,
  rows,
  isRequired = true,
  customError = false,
  defaultValue = '',
  ...rest
}) => {
  const {
    field,
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
    defaultValue,
  });

  return (
    <Box width={'100%'} sx={{ display: 'flex', flexDirection: 'column' }}>
      <TextField
        {...field}
        label={
          <Stack direction="row" gap={0.5}>
            {label}
            {isRequired && (
              <Typography sx={{ color: (theme) => theme.palette.primary.dark }}>
                *
              </Typography>
            )}
          </Stack>
        }
        fullWidth
        multiline
        rows={rows}
        error={invalid && !customError}
        {...rest}
        sx={{
          '& .MuiInputLabel-outlined': {
            color: (theme) => theme.palette.grey[300],
          },
          '& label.Mui-focused': {
            color: (theme) =>
              invalid ? theme.palette.primary.main : theme.palette.grey[400],
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: (theme) => theme.palette.grey[200],
            },
            '&:hover fieldset': {
              borderColor: (theme) => theme.palette.grey[300],
            },
            '&.Mui-focused fieldset': {
              borderColor: (theme) =>
                invalid ? theme.palette.primary.main : theme.palette.grey[400],
            },
          },
        }}
      />
      {invalid && !customError && (
        <FormHelperText
          sx={{
            color: (theme) => theme.palette.primary.dark,
            m: '3px 14px 0px',
          }}
        >
          {error?.message}
        </FormHelperText>
      )}
    </Box>
  );
};

export default HookTextArea;
